import { useEffect } from "react";
import { useNavigate } from "react-router";
import { Tooltip } from "react-tooltip";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { CheckCircleIcon, NoSymbolIcon } from "@heroicons/react/24/outline";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { clearSchool, setSchool } from "../../app/userSlice";
import { Loading } from "../../components/Loading";
import { ClassLinkOrCleverIcon } from "../../components/ClassLinkOrCleverIcon";
import SwitchToLegacy from "../../components/SwitchToLegacy";
import { IMutationError } from "../courses/Courses";
import {
	SelectSchoolViewModel,
	useGetSelectSchoolOptionsQuery,
	useMakeSchoolSelectionMutation,
} from "./schoolsApiSlice";

export default function SelectSchool() {
	const { firstName, lastName, isSchoolInTransition } = useAppSelector((state) => state.user);
	const { aspUrl, config } = useAppSelector((state) => state.site);

	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const schoolResults = useGetSelectSchoolOptionsQuery();
	const schools = schoolResults.data;

	const [selectSchool] = useMakeSchoolSelectionMutation();

	if (sessionStorage.getItem("prefersSpa") === "false") {
		window.location.href = `${aspUrl}/SignIn/SelectSchool`;
	}

	useEffect(() => {
		if (schools?.length === 1) {
			selectAndNavigateToSchool(schools[0]);
		}
	}, [schools]);

	const selectAndNavigateToSchool = (school: SelectSchoolViewModel) => {
		handleSelectSchool(school);
	};

	const hasClassLinkOrCleverSchool =
		schools?.some((s) => s.isClassLinkSchool || s.isCleverSchool) ?? false;

	const activeBodyTemplate = (row: SelectSchoolViewModel) => {
		const tooltipContent = `${row.hasExpired ? "Expired" : "Expires"}: ${row.expirationDate}`;

		return (
			<span className="flex justify-center">
				{row.active ? (
					<CheckCircleIcon
						className="size-6 text-green-500"
						data-tooltip-id={`enabled-${row.id}-tooltip`}
					/>
				) : (
					<NoSymbolIcon
						className="size-6 text-red-500"
						data-tooltip-id={`enabled-${row.id}-tooltip`}
					/>
				)}
				<Tooltip
					id={`enabled-${row.id}-tooltip`}
					place="top-start"
					content={tooltipContent}
				/>
			</span>
		);
	};

	const cleverOrClassLinkBodyTemplate = (row: SelectSchoolViewModel) => {
		return (
			<ClassLinkOrCleverIcon
				isClassLinkEntity={row.isClassLinkSchool}
				isCleverEntity={row.isCleverSchool}
			/>
		);
	};

	const schoolNameBodyTemplate = (row: SelectSchoolViewModel) => {
		return (
			<a
				id={`choose-school-${row.id}`}
				className="link"
				onClick={() => selectAndNavigateToSchool(row)}>
				{row.name}
			</a>
		);
	};

	const handleSelectSchool = (school: SelectSchoolViewModel) => {
		selectSchool(school.id)
			.unwrap()
			.then(() => {
				dispatch(
					setSchool({
						id: school.id,
						name: school.name,
					})
				);
				navigate("/adminHome");
			})
			.catch((error) => handleSelectSchoolError(error));
	};

	const handleSelectSchoolError = (error: IMutationError) => {
		dispatch(clearSchool());
		if (error.data) {
			console.log(`Error: ${error.data}`);
		} else {
			console.log(`Error: an unexpected error occurred.`);
		}
	};

	if (isSchoolInTransition) return null;

	return (
		<>
			<section className="text-s sm:text-left w-full">
				<div className="flex justify-between">
					<div className="w-3/4">
						<h1 className="font-bold">
							Welcome, {firstName} {lastName}
						</h1>
					</div>
				</div>
				<h2 className="text-s sm:text-left">Select a School</h2>
			</section>
			<section>
				<Loading loading={schoolResults.isLoading} />
				{schools && (
					<DataTable
						value={schools}
						dataKey="id"
						emptyMessage="No schools found"
						stripedRows
						className="tbl tbl-data tbl-datatable min-w-[40rem] cursor-default"
						style={{ paddingTop: "16px" }}>
						<Column
							header="Active"
							body={activeBodyTemplate}
							headerClassName="!w-12"
							className="!w-12 !text-center"></Column>
						{!config?.isUtahComposeSite && hasClassLinkOrCleverSchool && (
							<Column
								body={cleverOrClassLinkBodyTemplate}
								headerClassName="!px-2 w-4"
								className="!px-2"></Column>
						)}
						<Column header="School Name" body={schoolNameBodyTemplate}></Column>
						<Column header="Active Courses" field="numCourses"></Column>
						{!config?.isUtahComposeSite && (
							<Column header="Seat Count" field="numSeats"></Column>
						)}
					</DataTable>
				)}
			</section>
			<SwitchToLegacy link="/SignIn/SelectSchool" />
		</>
	);
}
